import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    Dimension,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';

export const toggleExtendedBifoldDoorHandler = (
    mesh: THREE.Group<THREE.Object3DEventMap>,
    meshPos: Dimension,
    variables: TemplateVariable,
    values: PreviewFormValues,
    exteriorThickness: number,
    carcaseThickness: number,
    isRightLShape = false,
    isLeftBifoldExtension = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for positional call-site compatibility; close/open now anchor to the door's live closed position
    depthSize = 0,
    thickness = 0,
    appliedInsertDepth = 0,
    appliedAlongLeg = 0
): AnimationHandler => {
    let initialPosition = {y: meshPos.y, x: meshPos.x};
    let openPosition = {
        y:
            -exteriorThickness -
            exteriorThickness +
            (carcaseThickness - exteriorThickness) +
            Number(values.cabinet_door_left) +
            appliedAlongLeg,
        x:
            meshPos.x +
            (values.cabinet_left_width - values.cabinet_right_depth) -
            Number(values.cabinet_door_left) -
            appliedInsertDepth,
    };

    if (variables?.isLShapedPantry && !isRightLShape) {
        const doorLeft = Number(values.cabinet_door_left);
        // Mirrors the isRightLShape pantry branch below (cabinet_right_width
        // - cabinet_left_depth), swapped left<->right. The previous formula
        // (cabinet_left_depth + exteriorThickness + doorWidth) never
        // referenced either leg's width, so it produced the same open
        // position regardless of the left/right width ratio. Unlike that
        // branch, no `+ doorThickness * 2` term here: this orientation isn't
        // a mirrored copy of the right-L template (same-frame, not
        // reflected - see project_co4836_pantry_right_left_path), so
        // reapplying that compensation double-counted it and overshot by
        // 2x thickness on X.
        openPosition.x =
            meshPos.x +
            (values.cabinet_left_width - values.cabinet_right_depth) -
            doorLeft;
        openPosition.y = -(doorLeft + carcaseThickness) - exteriorThickness;
    }

    let closePosition = {y: meshPos.y, x: meshPos.x};

    const closedHome = {x: mesh.position.x, y: mesh.position.y};

    if (isRightLShape) {
        const offsetThickness = variables?.isLShapedPantry
            ? (exteriorThickness + carcaseThickness) / 2
            : exteriorThickness;
        const offSetY =
            mesh.name === 'ShakerDoors'
                ? offsetThickness
                : offsetThickness - carcaseThickness;

        initialPosition = {
            y: closedHome.y,
            x: closedHome.x,
        };

        const openMeetAdjust = variables?.isLShapedPantry
            ? 0
            : variables?.is3DoorProduct
              ? carcaseThickness * 2 +
                (exteriorThickness - carcaseThickness) * 2
              : exteriorThickness * 2;
        const openPinchRelief = variables?.is3DoorProduct ? 0 : 2;
        openPosition = {
            y:
                values.cabinet_left_width -
                values.cabinet_right_depth -
                (values.cabinet_right_width - values.cabinet_left_depth) -
                +offSetY +
                Number(values.cabinet_door_right) +
                openMeetAdjust -
                openPinchRelief,
            x:
                meshPos.x +
                (values.cabinet_right_width - values.cabinet_left_depth) +
                exteriorThickness +
                thickness -
                Number(values.cabinet_door_right),
        };

        if (variables?.isLShapedPantry) {
            const doorThickness = exteriorThickness || carcaseThickness;
            openPosition.x =
                meshPos.x +
                (values.cabinet_right_width - values.cabinet_left_depth) -
                Number(values.cabinet_door_right) +
                doorThickness * 2;
        }

        closePosition = {
            x: closedHome.x,
            y: closedHome.y,
        };

        if (variables?.is3DoorProduct && !isLeftBifoldExtension) {
            initialPosition.y = closePosition.y = closedHome.y;
        }
    }

    const openDoor = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const openTween = new TWEEN.Tween(
                initialPosition,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(openPosition, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .delay(animationSpeed ? 100 : 0)
                .onUpdate(() => {
                    if (isRightLShape) mesh.position.x = initialPosition.x;
                    else mesh.position.y = initialPosition.y;
                });

            openTween.start();
        });

    const openDoorX = (animationSpeed = 700) =>
        new Promise<void>((resolve) => {
            const openTween = new TWEEN.Tween(
                initialPosition,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(openPosition, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    if (isRightLShape) mesh.position.y = initialPosition.y;
                    else mesh.position.x = initialPosition.x;
                });

            openTween.start();
        });

    const closeTween = (animationSpeed = 750) =>
        new Promise<void>((resolve) => {
            const tween = new TWEEN.Tween(
                initialPosition,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(closePosition, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    if (isRightLShape) mesh.position.x = initialPosition.x;
                    else mesh.position.y = initialPosition.y;
                });

            tween.start();
        });

    const closeTweenX = (animationSpeed = 1200) =>
        new Promise<void>((resolve) => {
            const tween = new TWEEN.Tween(
                initialPosition,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(closePosition, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .delay(75)
                .onUpdate(() => {
                    if (isRightLShape) mesh.position.y = initialPosition.y;
                    else mesh.position.x = initialPosition.x;
                });

            tween.start();
        });

    let initialRotation = {y: mesh.rotation.y};

    let openRotation = {y: -((90 * Math.PI) / 180)};
    let closeRotation = {y: 0};

    if (isRightLShape) {
        initialRotation = {y: mesh.rotation.y};
        openRotation = {y: 0};
        closeRotation = {y: -(90 * Math.PI) / 180};
    }

    if (isLeftBifoldExtension) {
        initialRotation = {y: mesh.rotation.y};
        openRotation = {y: -((60 * Math.PI) / 180)};
        closeRotation = {y: (90 * Math.PI) / 180};
    }

    const openDoor2 = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const openTween = new TWEEN.Tween(
                initialRotation,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(openRotation, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .delay(animationSpeed)
                .onUpdate(() => {
                    mesh.rotation.y = initialRotation.y;
                });

            openTween.start();
        });

    const closeTween2 = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const tween = new TWEEN.Tween(
                initialRotation,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(closeRotation, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .delay(isLeftBifoldExtension ? 300 : 0)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    mesh.rotation.y = initialRotation.y;
                });

            tween.start();
        });

    if (isLeftBifoldExtension) {
        return {
            open: [openDoor2],
            close: [closeTween2],
        };
    }

    return {
        open: [openDoor, openDoor2, openDoorX],
        close: [closeTween, closeTween2, closeTweenX],
    };
};
